import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Alert, Box, ButtonBase, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { UnmatchedIngredient, UnmatchedIngredients } from "apps/web/src/types";
import { useEffect, useMemo, useState } from "react";
import { capitalize } from "shared-ui/utils/capitalize";

type DisplayItem = {
  key: string;
  name: string;
  detail: string | null;
};

const formatAmount = (amount: number | null | undefined) => {
  if (amount == null || amount === 0) {
    return "";
  }
  // Round to 2 decimals only when needed (0.5 stays 0.5, 0.666… → 0.67)
  return String(Math.round(amount * 100) / 100);
};

const formatUnmatchedItem = ({ foodName, amount, servingName }: UnmatchedIngredient) => {
  const qty = formatAmount(amount);
  const serving = servingName?.trim() ?? "";
  const name = capitalize(foodName);
  if (qty && serving) {
    return { name, detail: `${qty} ${serving}` };
  }
  if (serving) {
    return { name, detail: serving };
  }
  if (qty) {
    return { name, detail: qty };
  }
  return { name, detail: null as string | null };
};

const toDisplayItems = (list: readonly UnmatchedIngredient[], prefix: string): DisplayItem[] =>
  list.map((ingredient, index) => {
    const formatted = formatUnmatchedItem(ingredient);
    return {
      key: `${prefix}:${formatted.name}:${formatted.detail ?? "none"}:${index}`,
      ...formatted,
    };
  });

interface UnmatchedIngredientsWarningProps {
  unmatchedIngredients: UnmatchedIngredients;
}

const UnmatchedChecklist = ({
  title,
  items,
  checkedKeys,
  onToggle,
}: {
  title: string;
  items: DisplayItem[];
  checkedKeys: ReadonlySet<string>;
  onToggle: (key: string) => void;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, maxWidth: "100%" }}>
    <Typography variant="body2Semibold">{title}</Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      {items.map(item => {
        const checked = checkedKeys.has(item.key);
        return (
          <FormControlLabel
            key={item.key}
            sx={{
              m: 0,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              alignItems: "flex-start",
              gap: 0.5,
              opacity: checked ? 0.65 : 1,
              "& .MuiFormControlLabel-label": {
                minWidth: 0,
                flex: 1,
              },
            }}
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={() => onToggle(item.key)}
                sx={{ p: 0.5, mt: -0.25, flexShrink: 0 }}
                inputProps={{ "aria-label": `Mark ${item.name} as added` }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  pt: 0.25,
                  textDecoration: checked ? "line-through" : "none",
                  color: checked ? "text.secondary" : "text.primary",
                  overflowWrap: "anywhere",
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {item.name}
                </Box>
                {item.detail ? (
                  <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                    {` · ${item.detail}`}
                  </Box>
                ) : null}
              </Typography>
            }
          />
        );
      })}
    </Box>
  </Box>
);

export const UnmatchedIngredientsWarning = ({ unmatchedIngredients }: UnmatchedIngredientsWarningProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<ReadonlySet<string>>(() => new Set());

  // REPLACE_STATE (e.g. new AI recipe) keeps this component mounted; clear checks so a
  // later set with the same item keys still shows as unchecked.
  useEffect(() => {
    setCheckedKeys(new Set());
  }, [unmatchedIngredients]);

  const unmatchedName = useMemo(
    () => toDisplayItems(unmatchedIngredients.unmatchedName, "name"),
    [unmatchedIngredients.unmatchedName]
  );
  const unmatchedServings = useMemo(
    () => toDisplayItems(unmatchedIngredients.unmatchedServings, "serving"),
    [unmatchedIngredients.unmatchedServings]
  );

  const allItems = useMemo(() => [...unmatchedName, ...unmatchedServings], [unmatchedName, unmatchedServings]);
  const totalCount = allItems.length;
  const checkedCount = allItems.reduce((count, item) => count + (checkedKeys.has(item.key) ? 1 : 0), 0);

  if (totalCount === 0 || checkedCount === totalCount) {
    return null;
  }

  const handleToggle = (key: string) => {
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <Alert
      severity="warning"
      icon={false}
      sx={{
        maxWidth: "100%",
        overflowX: "hidden",
        "& .MuiAlert-message": { width: "100%", minWidth: 0, overflow: "hidden", p: 0 },
      }}
    >
      <ButtonBase
        onClick={() => setCollapsed(value => !value)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Expand unmatched ingredients" : "Collapse unmatched ingredients"}
        disableRipple
        disableTouchRipple
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          width: "100%",
          textAlign: "left",
          borderRadius: 0.5,
          py: 0.25,
          cursor: "pointer",
          "&:hover": { bgcolor: "transparent" },
          "&:focus": { bgcolor: "transparent" },
          "&:active": { bgcolor: "transparent" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "warning.main",
            outlineOffset: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
          <WarningRoundedIcon color="warning" sx={{ flexShrink: 0, fontSize: 22 }} aria-hidden />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1Semibold" component="div">
              Some ingredients need attention
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`${checkedCount} of ${totalCount} checked off · check items as you add them`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary", flexShrink: 0 }} aria-hidden>
          {collapsed ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowUpIcon fontSize="small" />}
        </Box>
      </ButtonBase>
      {!collapsed && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5, ml: 4.75, minWidth: 0, maxWidth: "100%" }}>
          {unmatchedName.length > 0 && (
            <UnmatchedChecklist
              title="Foods not found — add manually"
              items={unmatchedName}
              checkedKeys={checkedKeys}
              onToggle={handleToggle}
            />
          )}
          {unmatchedServings.length > 0 && (
            <UnmatchedChecklist
              title="Units not found — pick a serving when adding"
              items={unmatchedServings}
              checkedKeys={checkedKeys}
              onToggle={handleToggle}
            />
          )}
        </Box>
      )}
    </Alert>
  );
};
